import { useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Building2, Star } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCompaniesAnalytics, type CompanyAnalyticsRow } from '@/services/admin/metrics.service'
import { cn } from '@/lib/utils'
import type { DashboardFilters as DashboardFiltersType } from './types'

type SortKey =
  | 'usersCount'
  | 'enrollmentsCompleted'
  | 'completionRate'
  | 'totalBadgesGranted'
  | 'avgBadgesPerUser'
  | 'averageRating'

interface Props {
  filters?: DashboardFiltersType
  onDrillDown?: (companyId: string) => void
}

/**
 * Tabela comparativa entre empresas (super_admin only). Suporta sort
 * por qualquer coluna numérica + drill-down clicando na empresa
 * (callback opcional pra setar o filtro de empresa do dashboard).
 */
export function CompaniesAnalyticsTable({ filters, onDrillDown }: Props) {
  const { data, isLoading, isError } = useCompaniesAnalytics(filters?.companyIds)
  const [sortKey, setSortKey] = useState<SortKey>('usersCount')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sorted = [...(data ?? [])].sort((a, b) => {
    const av = a[sortKey] as number
    const bv = b[sortKey] as number
    return sortDir === 'asc' ? av - bv : bv - av
  })

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="glass-card border-border/50">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Não foi possível carregar dados por empresa.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="glass-card border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="w-4 h-4 text-primary" />
          Comparativo por empresa
        </CardTitle>
        <CardDescription>
          {sorted.length} empresa{sorted.length !== 1 ? 's' : ''} ativa
          {sorted.length !== 1 ? 's' : ''} · clique no nome pra filtrar
        </CardDescription>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Nenhuma empresa ativa.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <SortableHeader
                    label="Usuários"
                    column="usersCount"
                    activeKey={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                  />
                  <SortableHeader
                    label="Completados"
                    column="enrollmentsCompleted"
                    activeKey={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                  />
                  <SortableHeader
                    label="Conclusão %"
                    column="completionRate"
                    activeKey={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                  />
                  <SortableHeader
                    label="Badges"
                    column="totalBadgesGranted"
                    activeKey={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                  />
                  <SortableHeader
                    label="Avg/user"
                    column="avgBadgesPerUser"
                    activeKey={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                  />
                  <SortableHeader
                    label="★ Rating"
                    column="averageRating"
                    activeKey={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <CompanyRow key={row.companyId} row={row} onDrillDown={onDrillDown} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function SortableHeader({
  label,
  column,
  activeKey,
  dir,
  onClick,
}: {
  label: string
  column: SortKey
  activeKey: SortKey
  dir: 'asc' | 'desc'
  onClick: (col: SortKey) => void
}) {
  const isActive = activeKey === column
  return (
    <TableHead className="text-right">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onClick(column)}
        className={cn(
          'h-7 -mr-3 gap-1 text-xs font-medium',
          isActive ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {label}
        {isActive ? (
          dir === 'desc' ? (
            <ArrowDown className="w-3 h-3" />
          ) : (
            <ArrowUp className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </Button>
    </TableHead>
  )
}

function CompanyRow({
  row,
  onDrillDown,
}: {
  row: CompanyAnalyticsRow
  onDrillDown?: (companyId: string) => void
}) {
  return (
    <TableRow className="hover:bg-card/30">
      <TableCell>
        <button
          onClick={() => onDrillDown?.(row.companyId)}
          className="flex items-center gap-2 hover:text-primary transition-colors text-left"
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={row.logo ?? undefined} />
            <AvatarFallback className="text-[10px]">
              {row.companyName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="font-medium text-sm truncate max-w-[180px]">{row.companyName}</span>
        </button>
      </TableCell>
      <TableCell className="text-right tabular-nums">{row.usersCount}</TableCell>
      <TableCell className="text-right tabular-nums">{row.enrollmentsCompleted}</TableCell>
      <TableCell className="text-right">
        <Badge
          variant="outline"
          className={cn(
            'tabular-nums',
            row.completionRate >= 50
              ? 'text-emerald-400 border-emerald-500/30'
              : row.completionRate >= 20
                ? 'text-amber-400 border-amber-500/30'
                : 'text-rose-400 border-rose-500/30'
          )}
        >
          {row.completionRate.toFixed(1)}%
        </Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums">{row.totalBadgesGranted}</TableCell>
      <TableCell className="text-right tabular-nums">{row.avgBadgesPerUser.toFixed(1)}</TableCell>
      <TableCell className="text-right">
        {row.averageRating > 0 ? (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            {row.averageRating.toFixed(1)}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}
